/**
 * Full-screen overlay view for mobile contexts
 *
 * Wraps BaseModal to always render full-screen on mobile (both web and native),
 * with content aligned top-left and a close icon at top-right.
 * On desktop, falls back to a configurable size (default "lg").
 *
 * Chrome colors follow the active theme automatically through semantic styling.
 * This replaces the previous mode-specific palette branch that rendered the
 * dark-mode overlay with insufficient contrast (SE-5578).
 */
import React, { ReactNode, memo } from "react";
import { Platform, ViewStyle } from "react-native";

import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { ifWeb } from "@/utils/platform";
import { XIcon } from "lucide-react-native";

/**
 * Style override for ModalBody to ensure the ScrollView expands to fill
 * available space — the base ModalBody sets flexGrow: 0 which clips content
 * in full-screen modals (SE-4007)
 */
const MODAL_BODY_FLEX_STYLE = { flexGrow: 1 } as const;

/** Supported modal size variants */
type ModalSize = "xs" | "sm" | "md" | "lg" | "xl" | "full";

/**
 * Desktop card width caps by size tier, mirroring the ui/modal ladder as
 * INLINE style (SE-5578): the motion-based `ModalContent` does not honor
 * width classes on web — empirically measured on staging, the note editor's
 * `md` card rendered ~215px (intrinsic content width) instead of the
 * ladder's 510px cap, and the new `xl` classes were equally inert. Only
 * inline style takes effect on this component (same mechanism as the
 * height cap below). `width: 90vw` + the per-tier cap reproduces
 * `w-[90%] max-w-[…px]` semantics.
 */
const DESKTOP_CARD_MAX_WIDTH: Record<Exclude<ModalSize, "full">, number> = {
  xs: 360,
  sm: 420,
  md: 510,
  lg: 640,
  xl: 750,
};

/**
 * Resolve modal size based on screen and platform
 * @param isSmallScreen - Whether the screen is mobile sized
 * @param desktopSize - Configured desktop size
 * @returns Resolved modal size
 */
const resolveModalSize = (
  isSmallScreen: boolean,
  desktopSize: ModalSize
): ModalSize => (isSmallScreen || Platform.OS !== "web" ? "full" : desktopSize);

/**
 * Check if the resolved modal size is full-screen
 * @param isSmallScreen - Whether the screen is mobile sized
 * @param desktopSize - Configured desktop size
 * @returns Whether the modal should render full-screen
 */
const isResolvedFullScreen = (
  isSmallScreen: boolean,
  desktopSize: ModalSize
): boolean => isSmallScreen || Platform.OS !== "web" || desktopSize === "full";

/**
 * Props for FullScreenOverlayView
 */
interface FullScreenOverlayViewProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  topInset: number;
  isSmallScreen: boolean;
  desktopSize?: ModalSize;
  headerContent?: ReactNode;
}

/**
 * FullScreenOverlayView renders a modal that is full-screen on mobile
 * @param props - View props
 * @param props.isOpen - Whether the overlay is visible
 * @param props.onClose - Callback to close the overlay
 * @param props.title - Header title text
 * @param props.children - Content to render inside the overlay
 * @param props.topInset - Safe area top inset for dynamic island clearance
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.desktopSize - Modal size on desktop (default "lg")
 * @param props.headerContent - Additional content in the header row
 * @returns Modal overlay that is full-screen on mobile, configurable on desktop
 */
const FullScreenOverlayView = ({
  isOpen,
  onClose,
  title,
  children,
  topInset,
  isSmallScreen,
  desktopSize = "lg",
  headerContent,
}: FullScreenOverlayViewProps) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    size={resolveModalSize(isSmallScreen, desktopSize)}
    useRNModal
    closeOnOverlayClick
  >
    <ModalBackdrop onPress={onClose} />
    <Stack
      className={
        isResolvedFullScreen(isSmallScreen, desktopSize) ? "size-full" : "p-2"
      }
    >
      <ModalContent
        className={`${
          isResolvedFullScreen(isSmallScreen, desktopSize)
            ? "size-full rounded-none border-0 border-none shadow-none"
            : /* SE-5578: desktop card radius per the Figma note-editor card
               * (node 4:55, rounded 12px) — scoped to the FullScreenOverlay
               * family rather than the app-wide Modal base. */
              "rounded-lg"
        } flex flex-col bg-surface-base p-0`}
        // eslint-disable-next-line no-restricted-syntax -- runtime style on bare re-export ModalContent atom (no UNSAFE_style prop): safe-area topInset, vw/vh viewport caps, and data-driven maxWidth; the motion-based ModalContent ignores width/height classes so only inline style takes effect (SE-5578)
        style={
          {
            paddingTop: isResolvedFullScreen(isSmallScreen, desktopSize)
              ? topInset
              : undefined,
            // SE-5578: desktop cards hug their content up to the 80vh cap.
            // The former minHeight: "60vh" floor forced short-content modals
            // (e.g. the note editor) into a tall, mostly-empty dialog — the
            // reported "unproportional/cramped" symptom. Figma's card
            // (node 4:55) is content-hugging with no minimum height.
            ...(Platform.OS === "web" &&
            !isResolvedFullScreen(isSmallScreen, desktopSize)
              ? ({
                  maxHeight: "80vh",
                  // SE-5578: width must be inline too — see
                  // DESKTOP_CARD_MAX_WIDTH. Without it the card hugs its
                  // intrinsic content width (~215px for the note editor).
                  width: "90vw",
                  maxWidth:
                    DESKTOP_CARD_MAX_WIDTH[
                      desktopSize === "full" ? "lg" : desktopSize
                    ],
                } as unknown as ViewStyle)
              : {}),
          } as ViewStyle
        }
      >
        <ModalHeader
          className={`${ifWeb("py-3 px-4", "border-b px-6 py-5")} flex-row items-center justify-between border-b border-outline-subtle`}
        >
          <Stack direction="horizontal" className="flex-1 items-center">
            <Heading variant="title-lg" className="text-content-primary">
              {title}
            </Heading>
          </Stack>
          <Stack direction="horizontal" className="gap-2">
            {headerContent}
            <Pressable
              onPress={onClose}
              className="rounded-full bg-surface-action p-2"
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <Icon as={XIcon} size="md" className="text-content-subtle" />
            </Pressable>
          </Stack>
        </ModalHeader>
        <ModalBody
          className="flex-1"
          // eslint-disable-next-line no-restricted-syntax -- static flexGrow:1 override on bare re-export ModalBody atom (no UNSAFE_style prop); className `grow` cannot win over ModalBody internal flexGrow:0 which clips full-screen content (SE-4007)
          style={MODAL_BODY_FLEX_STYLE}
          contentContainerClassName={ifWeb("p-4", "px-6 pb-6")}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator
        >
          {children}
        </ModalBody>
      </ModalContent>
    </Stack>
  </Modal>
);

FullScreenOverlayView.displayName = "FullScreenOverlayView";

export default memo(FullScreenOverlayView);
